import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import ErrorMessage from '../components/ui/ErrorMessage';
import mcqService from '../services/mcqService';

/**
 * MCQScorecard - Results page showing score and question-by-question breakdown
 * Displays overall score, correct/incorrect count, and detailed answers with explanations
 */
const MCQScorecard = () => {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  // Fetch results on mount
  useEffect(() => {
    if (isAuthenticated && attemptId) {
      fetchResults();
    }
  }, [isAuthenticated, attemptId]);

  /**
   * Fetch MCQ results
   */
  const fetchResults = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await mcqService.getMCQResults(attemptId);
      setResults(data);
    } catch (err) {
      console.error('Failed to fetch results:', err);
      setError(err.response?.data?.error || err.response?.data?.detail || 'Failed to load results. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <LoadingSpinner message="Loading results..." />
        <Footer />
      </div>
    );
  }

  // Error state
  if (error || !results) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <div className="container mx-auto max-w-4xl px-4 py-16">
          <ErrorMessage message={error || 'Results not found'} onRetry={error ? fetchResults : null} />
          <div className="text-center mt-6">
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Calculate incorrect count
  const incorrectCount = results.total_questions - results.correct_answers;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      <main className="flex-grow max-w-4xl mx-auto px-4 py-8 w-full">
        {/* Score Card Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg shadow-xl p-8 mb-8 text-white">
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-2">MCQ Scorecard</h1>
            <p className="text-blue-100">{results.mcq_title}</p>
          </div>

          {/* Score Display */}
          <div className="mt-8 text-center">
            <div className="inline-block bg-white/20 backdrop-blur-sm rounded-full px-8 py-4">
              <div className="text-6xl font-bold mb-2">{Number(results.score).toFixed(0)}%</div>
              <div className="text-lg text-blue-100">Your Score</div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mt-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
              <div className="text-2xl font-bold">{results.total_questions}</div>
              <div className="text-sm text-blue-100">Total Questions</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-300">{results.correct_answers}</div>
              <div className="text-sm text-blue-100">Correct</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-red-300">{incorrectCount}</div>
              <div className="text-sm text-blue-100">Incorrect</div>
            </div>
          </div>

          {/* Attempt Info */}
          <div className="mt-6 text-center text-sm text-blue-100">
            <span>Attempt #{results.attempt_number}</span>
            <span className="mx-2">•</span>
            <span>{new Date(results.completed_at).toLocaleString()}</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={() => navigate(`/mcq/${results.mcq}`)}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all font-semibold shadow-lg hover:shadow-xl"
          >
            Retake MCQ
          </button>
          <button
            onClick={() => navigate(-2)} // Go back 2 steps to course page
            className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
          >
            Back to Course
          </button>
        </div>

        {/* Question Breakdown */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Question Breakdown</h2>
          <div className="space-y-6">
            {results.question_answers.map((qa, index) => (
              <div
                key={qa.id}
                className={`bg-white rounded-lg shadow-md p-6 border-l-4 ${
                  qa.is_correct ? 'border-green-500' : 'border-red-500'
                }`}
              >
                {/* Question Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-grow">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="inline-block px-3 py-1 bg-gray-100 text-gray-800 text-sm font-semibold rounded-full">
                        Question {index + 1}
                      </span>
                      {qa.is_correct ? (
                        <span className="inline-flex items-center gap-1 text-green-600 font-semibold text-sm">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Correct
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-600 font-semibold text-sm">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          Incorrect
                        </span>
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">{qa.question_text}</h3>
                  </div>
                </div>

                {/* Options */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                  {['A', 'B', 'C', 'D', 'E'].map(option => {
                    const optionValue = qa[`option_${option.toLowerCase()}`];

                    // Skip if option E is empty (for backward compatibility)
                    if (option === 'E' && (!optionValue || optionValue.trim() === '')) {
                      return null;
                    }

                    const isCorrect = option === qa.correct_answer;
                    const isSelected = option === qa.selected_answer;

                    let bgColor = 'bg-gray-50 border-gray-200';
                    if (isCorrect) {
                      bgColor = 'bg-green-50 border-green-300';
                    } else if (isSelected && !isCorrect) {
                      bgColor = 'bg-red-50 border-red-300';
                    }

                    return (
                      <div
                        key={option}
                        className={`p-3 border-2 rounded-lg ${bgColor}`}
                      >
                        <div className="flex items-start gap-2">
                          <span className="font-bold flex-shrink-0">{option}.</span>
                          <span className="flex-grow">{optionValue}</span>
                          {isCorrect && (
                            <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          )}
                          {isSelected && !isCorrect && (
                            <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Answer Summary */}
                <div className="flex items-center gap-4 mb-3 text-sm">
                  <div>
                    <span className="text-gray-600">Your Answer: </span>
                    <span className={`font-semibold ${qa.is_correct ? 'text-green-600' : 'text-red-600'}`}>
                      {qa.selected_answer}
                    </span>
                  </div>
                  {!qa.is_correct && (
                    <div>
                      <span className="text-gray-600">Correct Answer: </span>
                      <span className="font-semibold text-green-600">{qa.correct_answer}</span>
                    </div>
                  )}
                </div>

                {/* Explanation */}
                {qa.explanation && (
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm font-medium text-blue-900 mb-1">Explanation:</p>
                    <p className="text-sm text-blue-800">{qa.explanation}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default MCQScorecard;
